/**
 * MuiProvidersDashboard - Optimized Providers Balance Dashboard
 *
 * OPTIMIZATION: Phase 11.5 - Dashboard optimization
 *
 * Changes made:
 * - Replaced getProviders() with fetchProviderDashboardData() optimized function
 * - fetchProviderDashboardData fetches only required fields (id, name, totals, ammount)
 * - Provider aggregation happens in api_helper, reducing duplicate processing
 * - Added refresh button for manual data reload
 *
 * Benefits:
 * - Significantly reduced data transfer (only essential fields)
 * - Faster initial load - pre-computed totals from API helper
 * - Summary stats pre-calculated server-side
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import { fetchProviderDashboardData } from "../../helpers/api_helper";

const MuiProvidersDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  document.title = t("Provider Balance") + " | FlexFlow ERP";

  const [providerTotals, setProviderTotals] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalProviders: 0,
    totalOrdersValue: 0,
    totalPaymentsValue: 0,
    totalBalance: 0,
    totalOrderCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  // ============================================================================
  // OPTIMIZED DATA FETCHING
  // Uses fetchProviderDashboardData() which fetches only required fields
  // ============================================================================
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchProviderDashboardData();
      setProviderTotals(data.providerTotals);
      setSummaryStats(data.summaryStats);
    } catch (error) {
      console.error("Error fetching provider dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter providers based on search
  const filteredProviders = useMemo(() => {
    if (!searchText) return providerTotals;
    const search = searchText.toLowerCase();
    return providerTotals.filter((p) => p.name?.toLowerCase().includes(search));
  }, [providerTotals, searchText]);

  // Chart data - top 10 providers
  const chartData = useMemo(() => {
    return providerTotals.slice(0, 10).map((provider) => ({
      name: provider.name?.length > 15 ? provider.name.substring(0, 15) + "..." : provider.name,
      fullName: provider.name,
      orders: parseFloat(provider.totalOrders.toFixed(2)),
      payments: parseFloat(provider.totalPayments.toFixed(2)),
      balance: parseFloat(provider.balance.toFixed(2)),
    }));
  }, [providerTotals]);

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Provider Name"),
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#8b5cf6",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {params.value?.charAt(0).toUpperCase() || "P"}
            </Avatar>
            <Typography variant="body2" fontWeight={500} color="#111827">
              {params.value || "-"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "orderCount",
        headerName: t("Total Orders"),
        width: 130,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: "#dbeafe",
              color: "#2563eb",
              fontWeight: 600,
              minWidth: 50,
            }}
          />
        ),
      },
      {
        field: "totalOrders",
        headerName: t("Orders Value"),
        width: 150,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={500} color="#111827">
            €{Number(params.value).toFixed(2)}
          </Typography>
        ),
      },
      {
        field: "totalPayments",
        headerName: t("Total Payments"),
        width: 150,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={500} color="#16a34a">
            €{Number(params.value).toFixed(2)}
          </Typography>
        ),
      },
      {
        field: "balance",
        headerName: t("Balance"),
        width: 150,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const value = Number(params.value);
          const isPositive = value > 0;
          const isNegative = value < 0;
          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isPositive && <TrendingUpIcon sx={{ fontSize: 16, color: "#dc2626" }} />}
              {isNegative && <TrendingDownIcon sx={{ fontSize: 16, color: "#16a34a" }} />}
              <Typography
                variant="body2"
                fontWeight={600}
                color={isPositive ? "#dc2626" : isNegative ? "#16a34a" : "#6b7280"}
              >
                €{Math.abs(value).toFixed(2)}
              </Typography>
            </Stack>
          );
        },
      },
    ],
    [t]
  );

  const handleRowClick = (params) => {
    navigate(`/provider-detail/${params.row.id}`);
  };

  const breadcrumbs = [
    { label: t("Views"), path: "/dashboard" },
    { label: t("Provider Balance") },
  ];

  if (loading) {
    return (
      <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Container maxWidth={false}>
        <MuiPageHeader
          title={t("Provider Balance")}
          subtitle={t("Overview of provider orders, payments and balances")}
          breadcrumbs={breadcrumbs}
          action={
            <IconButton
              size="small"
              onClick={fetchData}
              disabled={loading}
              sx={{ color: "#6b7280" }}
              title={t("Refresh")}
            >
              <RefreshIcon />
            </IconButton>
          }
        />

        {/* Summary Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", width: 44, height: 44 }}>
                    <StorefrontIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      {summaryStats.totalProviders}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Providers")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#2563eb", width: 44, height: 44 }}>
                    <ShoppingCartIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      {summaryStats.totalOrderCount}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Total Orders")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#fef3c7", color: "#d97706", width: 44, height: 44 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#111827">
                      €{summaryStats.totalOrdersValue.toFixed(0)}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Orders Value")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 44, height: 44 }}>
                    <PaymentsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#16a34a">
                      €{summaryStats.totalPaymentsValue.toFixed(0)}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Total Payments")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                bgcolor: summaryStats.totalBalance > 0 ? "#fef2f2" : "#ecfdf5",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      bgcolor: summaryStats.totalBalance > 0 ? "#fee2e2" : "#d1fae5",
                      color: summaryStats.totalBalance > 0 ? "#dc2626" : "#16a34a",
                      width: 44,
                      height: 44,
                    }}
                  >
                    <AccountBalanceWalletIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color={summaryStats.totalBalance > 0 ? "#dc2626" : "#16a34a"}
                    >
                      €{Math.abs(summaryStats.totalBalance).toFixed(0)}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Total Balance")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Chart Card */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6" }}>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Orders Value, Payments and Balance by Provider")}
            </Typography>
            <Typography variant="caption" color="#6b7280">
              {t("Top 10 providers by order value")}
            </Typography>
          </Box>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `€${value.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value, name) => [
                      `€${Number(value).toFixed(2)}`,
                      name === "orders"
                        ? t("Orders Value")
                        : name === "payments"
                        ? t("Payments")
                        : t("Balance"),
                    ]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: 20 }}
                    formatter={(value) =>
                      value === "orders"
                        ? t("Orders Value")
                        : value === "payments"
                        ? t("Payments")
                        : t("Balance")
                    }
                  />
                  <Bar dataKey="orders" fill="#3b82f6" name="orders" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="payments" fill="#16a34a" name="payments" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="balance" name="balance" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.balance >= 0 ? "#dc2626" : "#16a34a"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography color="#6b7280">{t("No data available")}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Provider Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid #f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="subtitle1" fontWeight={600} color="#111827">
                {t("Provider Totals")}
              </Typography>
              <Chip
                label={filteredProviders.length}
                size="small"
                sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", fontWeight: 600 }}
              />
            </Stack>
            <TextField
              size="small"
              placeholder={t("Search providers...")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  bgcolor: "#f9fafb",
                },
              }}
            />
          </Box>

          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={filteredProviders}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              disableColumnMenu
              rowHeight={60}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: "totalOrders", sort: "desc" }] },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  transition: "background-color 0.1s ease",
                  "&:hover": {
                    bgcolor: "#f9fafb",
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f3f4f6",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid #e5e7eb",
                },
              }}
              localeText={{
                noRowsLabel: t("No providers found"),
                MuiTablePagination: {
                  labelRowsPerPage: t("Rows per page"),
                },
              }}
            />
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default MuiProvidersDashboard;
